import React, { useState } from "react";
import { getCustomization } from "./customization";
import MarketSaleRowPro from "./MarketSaleRowPro";
import MiniTableTitleDisclaimer from "./MiniTableTitleDisclaimer";
import MiniTableSpecialDisclaimer from "./MiniTableSpecialDisclaimer";
import ReorderableTable, { getRowId } from "./ReorderableTable";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import MiniTableSubtitleRow from "./MiniTableSubtitleRow";
import { hasMeaningfulHtmlText } from "./utils";

/**
 * Shared display-only table. No edit state, no dialogs.
 * Used by both MiniTableProPrint and MiniTableProEditor.
 * @param {Object} props
 * @param {Array} props.miniTableProperties - Properties already sorted by _sortingOrder
 * @param {boolean} props.printVersion
 * @param {React.ReactNode} [props.renderBeforeTable] - Optional (e.g. edit button) rendered inside market-table-containers before the table
 * @param {boolean} [props.reorderable] - When true, first column is reorder handle and rows are draggable
 * @param {function} [props.onReorder] - Called with new ordered items (with _sortingOrder set) when drag ends
 * @param {function} [props.onDragStart] - Called when drag starts (for tutorial dismissal)
 * @param {boolean} [props.deletable] - When true, first column shows checkboxes for deletion
 * @param {boolean} [props.addable] - When true, first column shows checkboxes for add-to-table (same style as deletable, label "Add to Table")
 * @param {boolean} [props.viewOnly] - Read-only non-print mode used by "View Entire Property List" (no add/delete checkboxes, keep row labels)
 * @param {Set} [props.selectedIndices] - Set of selected row indices (used when deletable or addable)
 * @param {function} [props.onCheckboxChange] - Called with row index when checkbox is toggled (used when deletable or addable)
 * @param {function} [props.onAddToRow] - Called with count when user clicks to add more properties
 * @param {number} [props.maxRows] - Maximum number of rows to maintain (default 12)
 * @param {boolean} [props.editMode] - When true, shows Edit button in last column
 * @param {function} [props.onEditPropertyClick] - Called when Edit button is clicked, receives (property, index)
 * @param {boolean} [props.hideTitleAndSubtitle] - When true, hide title/subtitle and disclaimers
 * @param {boolean} [props.hideSubtitleRow] - When true, hide the date and listing-count subtitle row
 * @param {boolean} [props.allRowsManuallyAdded] - When true, pass isManuallyAdded to each row (e.g. Add dialog "manually added" section)
 * @param {function} [props.getRowIsManuallyAdded] - (index, item) => boolean; overrides allRowsManuallyAdded per row when provided. When addable, manual rows skip the add checkbox.
 * @param {function} [props.getRowIsDeleted] - (index, item) => boolean; when true, row shows DELETED property bar (e.g. add-from-list deleted section)
 * @param {boolean} [props.fromThirdPage] - When true, positions table at top: 405px via `top-405-mini-table`
 * @param {boolean} [props.moveQRAndTableDown] - When true, positions table at top: 446px (overrides fromThirdPage)
 */
function MiniTableProCore({
  miniTableProperties = [],
  printVersion,
  currentArea,
  quarterlyAreaTable,
  useExtendedTable,
  agentData,
  tableMonthStyles,
  renderBeforeTable,
  reorderable = false,
  onReorder,
  onDragStart,
  deletable = false,
  addable = false,
  viewOnly = false,
  selectedIndices,
  onCheckboxChange,
  onAddToRow,
  maxRows = 12,
  editMode = false,
  onEditPropertyClick,
  onViewPropertyDetails,
  mailProPropertyByListingId,
  hideTitleAndSubtitle = false,
  hideSubtitleRow = false,
  allRowsManuallyAdded = false,
  getRowIsManuallyAdded,
  getRowIsDeleted,
  miniTableTitle = "",
  miniTableSubTitle = "",
  miniTableDateRange = "",
  ...otherProps
}) {
  const hasAllActiveProperties =
    (miniTableProperties || []).every((prop) => prop.status === "Active") &&
    !editMode;
  const { mlsAreaID } = currentArea || {};

  // Single, shared hover preview (not one per row): centering it relative to
  // the whole table while still tracking the hovered row's vertical position
  // isn't expressible with CSS alone, since those are two different positioning
  // ancestors. Rows report their own offsetTop/offsetHeight on hover (measured
  // against .both-market-tables below, the nearest positioned ancestor) — with
  // `null` to clear on mouseleave, or `{ photoUrl, top }` on mouseenter (photoUrl
  // may itself be falsy, in which case the panel shows a "No photo found" message
  // instead of an image — still needs to report `top` so the panel appears).
  const [hoverPreview, setHoverPreview] = useState(null); // { photoUrl, top } | null
  const handleRowHoverPreview = (info) => {
    setHoverPreview(info);
  };

  const custom = getCustomization({
    currentArea,
    quarterlyAreaTable,
    agentData,
    miniTableProperties,
    currentSoldsLength: miniTableProperties?.length,
    ...otherProps,
  });

  const { tableStyles, marketSalesTitleStyles, hidePropertyTypeColumn } =
    custom;
  const hasMiniTableSubTitle = hasMeaningfulHtmlText(miniTableSubTitle);
  const subtitlePlainText = String(miniTableSubTitle || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  const isRecentActivitySubtitle =
    subtitlePlainText.includes("take a look at") &&
    subtitlePlainText.includes("recent activity");
  const showMarketInAMinuteSellingSubtitle =
    hideSubtitleRow && !isRecentActivitySubtitle;

  const showSpecialDisclaimers = !hideTitleAndSubtitle;

  const marketSaleRowProps = {
    mlsAreaID,
    isSingleTable: true,
    agentData,
    hidePropertyTypeColumn,
    hasAllActiveProperties,
    editMode,
    onEditPropertyClick,
    onViewPropertyDetails,
    mailProPropertyByListingId,
    onRowHoverPreview: handleRowHoverPreview,
    printVersion,
    showSpecialDisclaimers,
    hidePropertyBar: printVersion,
  };

  // Filter out empty/null items in print mode
  const displaySorted = printVersion
    ? (miniTableProperties || []).filter((item) => item && !item._isEmpty)
    : miniTableProperties;

  // Calculate how many more properties can be added
  const currentCount = (miniTableProperties || []).length;
  const remainingSlots = Math.max(0, maxRows - currentCount);

  // Read-only view-only mode needs property labels (deleted/manual/added) without checkbox actions.
  // Keep it separate from print mode and editable mode so behavior stays explicit.
  const viewModeTable = printVersion ? (
    <MarketSaleRowPro properties={displaySorted} {...marketSaleRowProps} />
  ) : viewOnly ? (
    <tbody>
      {displaySorted.map((item, index) => {
        const rowIsManuallyAdded = getRowIsManuallyAdded
          ? getRowIsManuallyAdded(index, item)
          : allRowsManuallyAdded;
        const rowIsDeleted = getRowIsDeleted
          ? getRowIsDeleted(index, item)
          : false;
        return (
          <MarketSaleRowPro
            key={`row-${index}`}
            properties={[item]}
            asSingleRow
            wrapRow
            rowIndex={index}
            isManuallyAdded={rowIsManuallyAdded}
            isDeleted={rowIsDeleted}
            {...marketSaleRowProps}
          />
        );
      })}
    </tbody>
  ) : (
    <tbody>
      {/* Render actual data rows */}
      {displaySorted.map((item, index) => (
        <MarketSaleRowPro
          key={`row-${index}`}
          properties={[item]}
          asSingleRow
          wrapRow
          rowIndex={index}
          isManuallyAdded={allRowsManuallyAdded}
          {...marketSaleRowProps}
        />
      ))}
      {/* Add empty rows at bottom */}
      {Array(remainingSlots)
        .fill(null)
        .map((_, index) => {
          const colSpan = hidePropertyTypeColumn ? 9 : 10;
          return (
            <tr key={`empty-row-${index}`} className="empty-row-placeholder">
              <td></td>
              <td colSpan={colSpan - 1} className="empty-row-cell">
                <div className="empty-row-button-wrapper">
                  <MailProButton
                    variant="filled"
                    label="Click here to add a property to this row"
                    onClick={() => onAddToRow?.(currentCount + index)}
                    customStyle={{
                      fontSize: "11px",
                      padding: "2px 8px",
                      textTransform: "none",
                      fontWeight: "normal",
                      minWidth: "auto",
                      whiteSpace: "nowrap",
                    }}
                  />
                </div>
              </td>
            </tr>
          );
        })}
    </tbody>
  );

  const hasCheckboxColumn = deletable || addable;
  const tableHeader = (
    <thead>
      <tr>
        <th className={hasCheckboxColumn ? "status-th-with-delete-label" : ""}>
          {deletable && (
            <>
              <span className="delete-from-table-label">
                <p>Delete</p>
                <p>From</p>
                <p>Table</p>
              </span>
              <span className="status-label">Status</span>
            </>
          )}
          {addable && !deletable && (
            <>
              <span className="delete-from-table-label">
                <p>Add</p>
                <p>to</p>
                <p>Table</p>
              </span>
              <span className="status-label">Status</span>
            </>
          )}
          {!hasCheckboxColumn && "Status"}
        </th>
        <th className="list-sale-date-th">
          <p>{hasAllActiveProperties ? "List " : "Sale "}</p>
          <p>Date</p>
        </th>
        {!hidePropertyTypeColumn && (
          <th className="property-type-th">
            <p>Property</p>
            <p>Type</p>
          </th>
        )}
        <th className="address-th">Address</th>
        <th className="list-price-th">List Price</th>
        <th className="sale-price-th">Sale Price</th>
        <th>
          <p>Days on</p>
          <p>Market</p>
        </th>
        <th className="mini-table-beds-header">
          <span className="mini-table-header-line">Beds</span>
        </th>
        <th className="mini-table-baths-header">
          <span className="mini-table-header-line">Full Baths /</span>
          <span className="mini-table-header-line">Half Baths</span>
        </th>
        <th>
          <p>Living</p>
          <p>SqFt</p>
        </th>
      </tr>
    </thead>
  );

  return (
    <div
      className={`${"print-ye2-mini-market-sales-table"} ${hideTitleAndSubtitle ? "review-mini-table-core" : ""} ${useExtendedTable ? "extended-table-container" : ""} ${addable ? "has-add-checkbox" : ""} ${tableStyles || ""} ${tableMonthStyles || ""}`}
    >
      {!hideTitleAndSubtitle && (
        <>
          <div className="market-sales-title-outer-container">
            <div
              className={`market-sales-title-container ${!hasMiniTableSubTitle && !showMarketInAMinuteSellingSubtitle ? "no-mini-table-subtitle" : ""}`}
            >
              {hideSubtitleRow ? (
                <p className="market-sales-subtitle-text market-in-a-min-table-title">
                  Here's What's Happening
                </p>
              ) : (
                miniTableTitle?.trim() && (
                  <p
                    className={`market-sales-subtitle-text ${marketSalesTitleStyles || ""}`}
                    dangerouslySetInnerHTML={{ __html: miniTableTitle }}
                  ></p>
                )
              )}
              {hideSubtitleRow && isRecentActivitySubtitle ? (
                <div
                  className="market-sales-subtitle-text market-in-a-min-table-subtitle"
                  dangerouslySetInnerHTML={{ __html: miniTableSubTitle }}
                />
              ) : showMarketInAMinuteSellingSubtitle ? (
                <p className="market-sales-subtitle-text market-in-a-min-table-subtitle">
                  See What's Selling in Your Neighborhood
                </p>
              ) : (
                hasMiniTableSubTitle && (
                  <MiniTableTitleDisclaimer
                    miniTableSubTitle={miniTableSubTitle}
                  />
                )
              )}
            </div>
          </div>

          {!hideSubtitleRow ? (
            <MiniTableSubtitleRow
              miniTableProperties={miniTableProperties}
              miniTableDateRange={miniTableDateRange}
              mlsAreaID={mlsAreaID}
            />
          ) : null}
        </>
      )}

      <div className="market-table-containers">
        {renderBeforeTable}
        <div className="both-market-tables">
          <table
            className={`regular-sales-table ${
              hidePropertyTypeColumn ? "hide-property-type" : ""
            } ${reorderable ? "has-reorder-handle" : ""} ${hasCheckboxColumn ? "has-delete-handle" : ""} ${
              editMode ? "has-edit-button" : ""
            }`}
          >
            {(miniTableProperties || []).length >= 1 ? tableHeader : null}
            {reorderable ? (
              <ReorderableTable
                miniTableProperties={miniTableProperties}
                marketSaleRowProps={marketSaleRowProps}
                hidePropertyTypeColumn={hidePropertyTypeColumn}
                onReorder={onReorder}
                onDragStart={onDragStart}
              />
            ) : hasCheckboxColumn ? (
              <tbody>
                {(miniTableProperties || []).map((item, index) => {
                  const isChecked = selectedIndices?.has(index);
                  const rowIsManuallyAdded = getRowIsManuallyAdded
                    ? getRowIsManuallyAdded(index, item)
                    : allRowsManuallyAdded;
                  const skipAddCheckbox = addable && rowIsManuallyAdded;
                  const showCheckbox =
                    deletable || (addable && !skipAddCheckbox);
                  const checkboxHandle = showCheckbox ? (
                    <span className="handle-in-cell">
                      <label className="checkbox-wrapper">
                        <input
                          type="checkbox"
                          checked={!!isChecked}
                          onChange={() => onCheckboxChange?.(index)}
                          className="checkbox-input"
                          aria-label={
                            deletable
                              ? `Select property ${index + 1} for deletion`
                              : `Select property ${index + 1} to add to table`
                          }
                        />
                        <span
                          className={`checkbox-custom ${isChecked ? "checked" : ""}`}
                        >
                          {isChecked && (
                            <span className="checkbox-x">
                              {deletable ? "✕" : "✓"}
                            </span>
                          )}
                        </span>
                      </label>
                    </span>
                  ) : null;
                  const rowMarkedClass =
                    deletable && isChecked
                      ? "row-marked-for-deletion"
                      : addable && isChecked
                        ? "row-marked-for-addition"
                        : addable && item?._isDeleted
                          ? "row-marked-for-deletion"
                          : "";
                  const rowIsDeleted = getRowIsDeleted
                    ? getRowIsDeleted(index, item)
                    : false;
                  return (
                    <MarketSaleRowPro
                      key={getRowId(item, index)}
                      properties={[item]}
                      asSingleRow
                      wrapRow
                      rowIndex={index}
                      deleteHandle={checkboxHandle}
                      isDeleting={deletable && !!isChecked}
                      isAdding={addable && !skipAddCheckbox && !!isChecked}
                      isDeleted={rowIsDeleted}
                      isManuallyAdded={rowIsManuallyAdded}
                      additionalRowClassName={rowMarkedClass}
                      {...marketSaleRowProps}
                    />
                  );
                })}
              </tbody>
            ) : (
              viewModeTable
            )}
          </table>
          {/* Stays mounted (visibility toggled) rather than being added/removed
              on every hover, so the browser can reuse its already-decoded
              image instead of re-fetching/re-decoding from S3 each time. */}
          <div
            className={`mini-table-photo-hover-preview ${hoverPreview && !hoverPreview.photoUrl ? "mini-table-photo-hover-preview-empty" : ""}`}
            style={{
              top: hoverPreview?.top ?? 0,
              visibility: hoverPreview ? "visible" : "hidden",
            }}
          >
            {hoverPreview?.photoUrl ? (
              <img src={hoverPreview.photoUrl} alt="Property" />
            ) : (
              <div className="mini-table-no-photo-text">No photo found</div>
            )}
          </div>
        </div>
        {showSpecialDisclaimers && (
          <MiniTableSpecialDisclaimer custom={custom} />
        )}
      </div>
    </div>
  );
}

export default MiniTableProCore;
